import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectIsUserLeader } from '../../redux/organization/organizationSlice';
import { inviteMember } from '../../services/api/organizationService';

// 旧 MemberInviteForm.scss を内製化した Tailwind 定数。
// .error-message は globals.css の同名定義（text-align:center）が効いていたため、
// 自ファイルの高特異度ルールとマージした実効値を書いてグローバル依存を断っている。
const FORM_BOX =
  'bg-white rounded-lg shadow-[0_2px_4px_rgba(0,0,0,0.1)] p-5 md:flex md:flex-wrap md:gap-[15px]';
const FORM_GROUP = 'mb-[15px] md:flex-1 md:min-w-[220px] md:mb-0';
const FORM_LABEL = 'block mb-[5px] font-medium';
const FORM_INPUT =
  'w-full p-2.5 border border-[#ddd] rounded focus:outline-none focus:border-primary ' +
  'focus:shadow-[0_0_0_2px_rgba(97,111,201,0.1)] disabled:bg-[#f5f5f5] disabled:cursor-not-allowed';
const MSG_BASE = 'my-2.5 px-3 py-2 rounded text-[0.875rem] md:basis-full';
const ERROR_MSG = `${MSG_BASE} text-[#d32f2f] bg-[#ffebee] text-center`;
const SUCCESS_MSG = `${MSG_BASE} text-[#388e3c] bg-[#e8f5e9]`;
const INVITE_BUTTON =
  'block w-full p-3 bg-primary text-white border-none rounded font-medium cursor-pointer ' +
  'mt-5 hover:bg-[#3F4FB8] disabled:bg-[#cccccc] disabled:cursor-not-allowed ' +
  'md:w-auto md:mt-[25px] md:px-5 md:py-2.5';

interface MemberInviteFormProps {
  onSuccess: () => void;
}

export const MemberInviteForm: React.FC<MemberInviteFormProps> = ({ onSuccess }) => {
  const isLeader = useSelector(selectIsUserLeader);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // リーダー権限がない場合は表示しない
  if (!isLeader) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setError('メールアドレスを入力してください');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setError(null);
      setSuccess(null);
      
      // メンバー招待APIを呼び出し
      await inviteMember({ email, name });
      
      // 成功時の処理
      setEmail('');
      setName('');
      setSuccess('メンバーを招待しました');
      onSuccess(); // 親コンポーネントに成功を通知
    } catch (err) {
      setError(err instanceof Error ? err.message : '招待に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mb-[30px]" id="member-invite-form" data-tutorial="organization-invite">
      <h2 className="text-[1.5rem] mb-5 text-primary">メンバー招待</h2>
      <form onSubmit={handleSubmit} className={FORM_BOX}>
        <div className={FORM_GROUP}>
          <label htmlFor="email" className={FORM_LABEL}>メールアドレス (必須)</label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="example@example.com"
            className={FORM_INPUT}
            disabled={isSubmitting}
            required
          />
        </div>
        
        <div className={FORM_GROUP}>
          <label htmlFor="name" className={FORM_LABEL}>名前 (任意)</label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="山田 太郎"
            className={FORM_INPUT}
            disabled={isSubmitting}
          />
        </div>
        
        {error && <div className={ERROR_MSG}>{error}</div>}
        {success && <div className={SUCCESS_MSG}>{success}</div>}
        
        <button 
          type="submit" 
          className={INVITE_BUTTON}
          disabled={isSubmitting}
        >
          {isSubmitting ? '招待中...' : 'メンバーを招待'}
        </button>
      </form>
    </div>
  );
};
